/// <amd-module name="Wit/WorkItems/WorkItemActions" />
import { effects } from "redux-saga";
import { ActionsUnion, createAction } from "redux/Utils/ActionHelper";
import { FieldStatusFlags } from "Wit/Clients/Internal/Contracts/SocialWorkItem";
import { FieldValueMap, FieldValueType } from "Wit/WorkItemTypes/Contracts";
import { IFieldUpdate, IWorkItem, IWorkItemChange, IWorkItemLinkUpdate, IWorkItemRelations, WorkItemStatus } from "./WorkItemContracts";

export const enum WorkItemActionTypes {
    WorkItemsLoadRequested = "WorkItems/WorkItemsLoadRequested",
    WorkItemsLoadFinished = "WorkItems/WorkItemsLoadFinished",

    LoadedWorkItems = "WorkItems/LoadedWorkItems",
    UpdateWorkItemsStatus = "WorkItems/UpdateWorkItemsStatus",

    WorkItemChangeRequested = "WorkItems/WorkItemChangeRequested",
    /** Work item change succeeded and the new values are persisted on the server */
    WorkItemChangeSucceeded = "WorkItems/WorkItemChangeSucceeded",

    UnblockWorkItemChange = "WorkItems/UnblockChange",

    WorkItemUpdated = "WorkItems/WorkItemUpdated",

    LoadAllowedValues = "WorkItems/LoadAllowedValues",
    LoadAllowedValuesSucceeded = "WorkItems/LoadAllowedValuesSucceeded",

    LoadFieldFlags = "WorkItems/LoadFieldFlags",
    LoadFieldFlagsSucceeded = "WorkItems/LoadFieldFlagsSucceeded",

    WorkItemCreateRequested = "WorkItems/WorkItemCreateRequested",
    WorkItemCreateSucceeded = "WorkItems/WorkItemCreateSucceeded",
    WorkItemCreateFailed = "WorkItems/WorkItemCreateFailed",

    WorkItemDeleteRequested = "WorkItems/WorkItemDeleteRequested",
    WorkItemDeleteFailed = "WorkItems/WorkItemDeleteFailed",
    WorkItemDeleteSucceeded = "WorkItems/WorkItemDeleteSucceeded",

    WorkItemRestoreRequested = "WorkItems/WorkItemRestoreRequested",
    WorkItemRestoreFailed = "WorkItems/WorkItemRestoreFailed",
    WorkItemRestoreSucceeded = "WorkItems/WorkItemRestoreSucceeded",

    InitializeNewWorkItem = "WorkItems/InitializeNewWorkItem",
    NewWorkItemInitialized = "WorkItems/NewWorkItemInitialized",
    NewWorkItemInitializationFailed = "WorkItems/NewWorkItemInitializationFailed",
    RemoveTemporaryWorkItem = "WorkItems/RemoveTemporaryWorkItem",

    setWorkItemError = "WorkItems/SetWorkItemError",
    clearWorkItemError = "WorkItems/clearWorkItemError"
}

export const WorkItemActions = {
    workItemsLoadRequested: (workItemsRequestData: { workItemId: number; projectId: string }[], fields?: string[], useCached?: boolean) =>
        createAction(WorkItemActionTypes.WorkItemsLoadRequested, { workItemsRequestData, fields, useCached }),
    workItemsLoadFinished: (loadedWorkItems: IWorkItem[], failedWorkItemIds: number[]) =>
        createAction(WorkItemActionTypes.WorkItemsLoadFinished, { loadedWorkItems, failedWorkItemIds }),

    /** Notify that new work items have been loaded */
    loadWorkItems: (workItems: IWorkItem[]) => createAction(WorkItemActionTypes.LoadedWorkItems, { workItems }),

    /** Manually update the work item status */
    updateWorkItemsStatus: (payload: { workItemId: number; status: WorkItemStatus; error?: string }[]) =>
        createAction(WorkItemActionTypes.UpdateWorkItemsStatus, payload),

    /** Request a field change for a work item */
    fieldValueChangeRequested: (workItemId: number, fieldRefName: string, value: FieldValueType) =>
        WorkItemActions.workItemChangeRequested(workItemId, [{ fieldRefName, value }], undefined, false),

    /**
     * Request a work item change including field or link changes
     *
     * @param workItemId Id of the work item
     * @param fieldChanges
     * @param linkChanges
     * @param isBlocked If set, don't immediately send the server request but wait for a matching UnblockWorkItemChange action
     */
    workItemChangeRequested: (workItemId: number, fieldChanges?: IFieldUpdate[], linkChanges?: IWorkItemLinkUpdate[], isBlocked?: boolean) =>
        createAction(WorkItemActionTypes.WorkItemChangeRequested, {
            workItemId,
            workItemChange: {
                fieldChanges,
                linkChanges,
                isBlocked,
                scenarioStart: Date.now()
            } as IWorkItemChange
        }),
    /** Notify that a work item change was successfully saved on the server */
    workItemChangeSucceeded: (workItemId: number, workItemChange: IWorkItemChange) =>
        createAction(WorkItemActionTypes.WorkItemChangeSucceeded, { workItemId, workItemChange }),

    /** Unblock a work item  */
    unblockWorkItemChange: (workItemId: number) =>
        createAction(WorkItemActionTypes.UnblockWorkItemChange, {
            workItemId
        }),

    /** Request allowed values for a field */
    loadAllowedValuesRequested: (workItemId: number, fieldRefName: string) =>
        createAction(WorkItemActionTypes.LoadAllowedValues, { workItemId, fieldRefName }),
    /** Action to put allowed values into state */
    loadAllowedValuesSucceeded: (workItemId: number, fieldRefName: string, allowedValues: string[]) =>
        createAction(WorkItemActionTypes.LoadAllowedValuesSucceeded, { workItemId, fieldRefName, allowedValues }),

    /** Load field status flags (e.g., required, readonly) for a given work item */
    loadFieldFlags: (workItemId: number) => createAction(WorkItemActionTypes.LoadFieldFlags, { workItemId }),
    /** Action to put field flags into state */
    loadFieldFlagsSucceeded: (workItemId: number, fieldFlags: { [referenceName: string]: FieldStatusFlags }) =>
        createAction(WorkItemActionTypes.LoadFieldFlagsSucceeded, { workItemId, fieldFlags }),

    /** Use this to request that a work item should be created. */
    workItemCreateRequested: (projectId: string, tempWorkItemId: number) =>
        createAction(WorkItemActionTypes.WorkItemCreateRequested, { projectId, tempWorkItemId }),
    workItemCreateFailed: (tempWorkItemId: number, error: string) =>
        createAction(WorkItemActionTypes.WorkItemCreateFailed, { tempWorkItemId, error }),
    workItemCreateSucceeded: (workItemId: number, tempWorkItemId?: number) =>
        createAction(WorkItemActionTypes.WorkItemCreateSucceeded, { workItemId, tempWorkItemId }),

    /** Delete a work item */
    workItemDeleteRequested: (projectId: string, workItemId: number) =>
        createAction(WorkItemActionTypes.WorkItemDeleteRequested, { projectId, workItemId }),
    workItemDeleteFailed: (workItemId: number, error: string) => createAction(WorkItemActionTypes.WorkItemDeleteFailed, { workItemId, error }),
    /** Notify that a work item has been deleted successfully */
    workItemDeleteSucceeded: (workItemId: number) => createAction(WorkItemActionTypes.WorkItemDeleteSucceeded, { workItemId }),

    /** Restore a deleted work item */
    workItemRestoreRequested: (workItemId: number, projectId: string) =>
        createAction(WorkItemActionTypes.WorkItemRestoreRequested, { workItemId, projectId }),
    workItemRestoreFailed: (workItemId: number, error: string) => createAction(WorkItemActionTypes.WorkItemRestoreFailed, { workItemId, error }),
    /** Notify that a work item has been restored successfully */
    workItemRestoreSucceeded: (workItemId: number) => createAction(WorkItemActionTypes.WorkItemRestoreSucceeded, { workItemId }),

    /** Use this to initialize a new temporary workItem. 'NewWorkItemInitialized' will be fired once successful */
    initializeNewWorkItem: (
        projectId: string,
        workItemTypeName: string,
        initialValues?: FieldValueMap,
        initialLinks?: IWorkItemLinkUpdate[],
        tempWorkItemId?: number
    ) =>
        createAction(WorkItemActionTypes.InitializeNewWorkItem, {
            tempWorkItemId,
            projectId,
            workItemTypeName,
            initialValues,
            initialLinks
        }),
    newWorkItemInitialized: (temporaryWorkItemId: number) => createAction(WorkItemActionTypes.NewWorkItemInitialized, temporaryWorkItemId),
    /** New work item initialization failed. For example, due to work item type not loaded. */
    newWorkItemInitializationFailed: (temporaryWorkItemId: number, errorMessage: string) =>
        createAction(WorkItemActionTypes.NewWorkItemInitializationFailed, { temporaryWorkItemId, errorMessage }),

    /**
     * Use this event to notify when a workItem has been updated and saved to the server. This is the new source of truth which
     * isn't checked further, so be careful.
     */
    workItemUpdated: (workItemId: number, fields?: { [fieldRefName: string]: FieldValueType }, workItemRelations?: IWorkItemRelations) =>
        createAction(WorkItemActionTypes.WorkItemUpdated, { workItemId, fields, workItemRelations }),

    /** Remove temporary workitem from store */
    removeTemporaryWorkItem: (workItemId: number) => createAction(WorkItemActionTypes.RemoveTemporaryWorkItem, { workItemId }),

    setWorkItemError: (workItemId: number, error: string) => createAction(WorkItemActionTypes.setWorkItemError, { workItemId, error }),
    clearWorkItemError: (workItemId: number) => createAction(WorkItemActionTypes.clearWorkItemError, { workItemId })
};

export type WorkItemActions = ActionsUnion<typeof WorkItemActions>;

export function getWorkItemChangeCompletedPredicate(workItemIdToCheck: number): effects.ActionPattern {
    return (action: WorkItemActions) => {
        switch (action.type) {
            case WorkItemActionTypes.WorkItemChangeSucceeded: {
                const { workItemId } = action.payload;
                if (workItemId === workItemIdToCheck) {
                    return true;
                }
            }
        }
        return false;
    };
}
